import {Extension} from '@tiptap/core';
import {PluginKey} from '@tiptap/pm/state';
import {ReactRenderer} from '@tiptap/react';
import Suggestion, {type SuggestionProps} from '@tiptap/suggestion';
import AutomationVariablePicker, {type AutomationVariablePickerHandle} from './AutomationVariablePicker';
import {automationOptionContent, type AutomationVariableOption} from './automationVariableOptions';

export const automationVariableSlashKey = new PluginKey('automationVariableSlash');

/** A slash in a closing tag, self-closing tag, attribute or URL is ordinary HTML source. */
export const allowsAutomationSlash = (textBefore: string, query: string, htmlSource: boolean) =>
    !/[<>/"'=&?#]/.test(query) && (!htmlSource || !/<[^>]*$/.test(textBefore));

interface Options {
    getOptions: () => AutomationVariableOption[];
    purpose: 'insert-placeholder' | 'select-step-value';
    htmlSource: boolean;
}

const AutomationVariableSlashCommand = Extension.create<Options>({
    name: 'automationVariableSlash',
    addOptions() { return {getOptions: () => [], purpose: 'insert-placeholder', htmlSource: false}; },
    addProseMirrorPlugins() {
        return [Suggestion<AutomationVariableOption>({
            editor: this.editor,
            pluginKey: automationVariableSlashKey,
            char: '/',
            allowedPrefixes: [' ', '\n', '\t', '>'],
            allow: ({state, range}) => allowsAutomationSlash(
                state.doc.textBetween(0, range.from, '', '\n'),
                state.doc.textBetween(range.from + 1, range.to),
                this.options.htmlSource,
            ),
            // The picker owns filtering and needs the full catalogue for empty states.
            items: () => this.options.getOptions(),
            command: ({editor, range, props}) => {
                editor.chain().focus().insertContentAt(range, automationOptionContent(props)).run();
            },
            render: () => {
                let component: ReactRenderer<AutomationVariablePickerHandle> | null = null;
                let unmount: (() => void) | undefined;
                const pickerProps = (props: SuggestionProps<AutomationVariableOption>) => ({
                    options: props.items, query: props.query, onSelect: props.command,
                    purpose: this.options.purpose,
                });
                return {
                    onStart: props => {
                        component = new ReactRenderer(AutomationVariablePicker, {
                            editor: this.editor, props: pickerProps(props),
                        });
                        // Above the node dialog; info details sit one layer higher.
                        component.element.style.zIndex = '1301';
                        unmount = props.mount(component.element);
                    },
                    onUpdate: props => component?.updateProps(pickerProps(props)),
                    onKeyDown: ({event}) => {
                        const handled = component?.ref?.onKeyDown(event) ?? false;
                        // Escape belongs to this menu, not the containing node dialog.
                        if (handled || event.key === 'Escape') event.stopPropagation();
                        return handled;
                    },
                    onExit: () => {
                        unmount?.();
                        component?.destroy();
                        component = null;
                    },
                };
            },
        })];
    },
});

export default AutomationVariableSlashCommand;
